// -----------------------------------------------------
// Helper functions (must be outside the class)
// -----------------------------------------------------
function getCurrentVariantId(form) {
  const idInput = form.querySelector('[name="id"]');
  return idInput?.value || null;
}

function getCurrentVariantTitle(form, currentVariantId) {
  // 1️⃣ Try to read from product JSON (if available)
  let productData = null;
  const jsonEl =
    document.querySelector('[data-product-json]') ||
    document.querySelector('[id^="ProductJson-"]');

  if (jsonEl) {
    try {
      productData = JSON.parse(jsonEl.textContent);
    } catch (e) {}
  }

  if (productData?.variants && currentVariantId) {
    const match = productData.variants.find(
      (v) => String(v.id) === String(currentVariantId)
    );
    if (match?.title && match.title !== "Default Title") return match.title;
  }

  // 2️⃣ If no JSON, read visible variant selectors (works in all cases)
  const optionInputs = Array.from(
    form.querySelectorAll('[name^="options["], select[name^="options["]')
  );
  const values = [];

  optionInputs.forEach((input) => {
    if (input.type === "radio") {
      if (input.checked) values.push(input.value);
    } else if (input.tagName === "SELECT") {
      const val = input.selectedOptions?.[0]?.text?.trim();
      if (val) values.push(val);
    } else if (input.value) {
      values.push(input.value);
    }
  });

  // 3️⃣ Final fallback
  if (!values.length) {
    const variantSelect = form.querySelector("select[name='id']");
    const optText = variantSelect?.selectedOptions?.[0]?.text?.trim();
    if (optText && optText !== "Default Title") return optText;
  }

  return values.length ? values.join(" / ") : "Default";
}
// -----------------------------------------------------
// MAIN PRODUCT-FORM COMPONENT
// -----------------------------------------------------
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

        this.cart =
          document.querySelector('cart-notification') ||
          document.querySelector('cart-drawer');

        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer'))
          this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        config.body = formData;

        // Main variant id
        const mainVariantId = getCurrentVariantId(this.form);

        // Unique link between main item and gift-wrap item
        const uniqueKey = `${mainVariantId}-${Date.now()}`;
        formData.append('properties[_Gift_Link]', uniqueKey);

        // -----------------------------------------------------
        // MAIN ADD-TO-CART CALL
        // -----------------------------------------------------
        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector('.sold-out-message');

              if (!soldOutMessage) return;

              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            // -----------------------------------------------------
            // 🎁 ADD GIFT WRAP LOGIC
            // -----------------------------------------------------

            const cb = document.querySelector('#giftbox-checkbox');
            const giftVariantId =
              cb?.checked
                ? document.querySelector('#giftbox-variant-id')?.value
                : null;

            if (giftVariantId) {
              const mainVariantTitle = getCurrentVariantTitle(
                this.form,
                mainVariantId
              );

              const giftProductTitle =
                document.querySelector('.product__title')?.textContent?.trim() ||
                document.title;

              const giftPayload = {
                items: [
                  {
                    id: parseInt(giftVariantId, 10),
                    quantity: 1,
                    properties: {
                      _For_Product: giftProductTitle,
                      _For_Variant: mainVariantTitle,
                      _For_Variant_ID: mainVariantId,
                      Gift_Wrap: 'Yes',
                      _Gift_Link: uniqueKey,
                    },
                  },
                ],
              };

              await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(giftPayload),
              });
            }

            // -----------------------------------------------------
            // Continue normal Dawn cart update flow
            // -----------------------------------------------------
            const startMarker = CartPerformance.createStartingMarker(
              'add:wait-for-subscribers'
            );

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  'add:wait-for-subscribers',
                  startMarker
                );
              });

            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      'add:paint-updated-sections',
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );

              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => console.error(e))
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty'))
              this.cart.classList.remove('is-empty');

            if (!this.error)
              this.submitButton.removeAttribute('aria-disabled');

            this.querySelector('.loading__spinner').classList.add('hidden');
            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      // -----------------------------------------------------
      // Helpers
      // -----------------------------------------------------
      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) this.errorMessage.textContent = errorMessage;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent =
            window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
